import { WModalButton } from "@/components/button/WToolButton.tsx";
import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { LongDate } from "@/components/l10n/DateFormat.tsx";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { SectionHeader } from "@/components/SectionHeader.tsx";
import { DetailsTable } from "@/components/table/DetailsTable.tsx";
import { DetailsTableRow } from "@/components/table/DetailsTableRow.tsx";
import { WTable, WTableBody } from "@/components/table/WTable.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import type { ManagedServiceWithAssigned } from "@/modules/services/joinAssignedQuery.ts";
import { getReplicasByType } from "@/modules/services/serviceEntityUtils.ts";
import { rebuildServiceMutation } from "@/modules/services/servicesQuery.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { apiDatetimeToDate } from "@warrenio/api-spec/conversion";
import type { ManagedServicesVmBody } from "@warrenio/api-spec/spec.oats.gen";
import { first, sortBy } from "remeda";

export function BackupsContent({ service }: { service: ManagedServiceWithAssigned }) {
    const { uuid, resources } = service;
    const vms = resources.filter((item) => item.resource_type === "vm");
    const isMultiNode = vms.length > 1;

    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader
                title={<Trans>Backups</Trans>}
                description={
                    <Trans>
                        Automatic system-level backups are enabled. Use the backup images to restore your service.
                        Backups will be stored for 7 days, meaning you will have 7 latest backups.
                    </Trans>
                }
            />

            {vms.map((item, index) => (
                <div key={item.resource_id} className="VStack gap-2">
                    {isMultiNode && (
                        <h3 className="font-size-default">
                            <Trans>Node</Trans> {index + 1}
                        </h3>
                    )}
                    <BackupsTable vm={item} isMultiNode={isMultiNode} service_package_uuid={uuid} />
                </div>
            ))}
        </ContentPane>
    );
}

function sortByCreatedAtDesc<T extends { created_at: string }>(items: T[]) {
    return sortBy(items, [(item) => apiDatetimeToDate(item.created_at).getTime(), "desc"]);
}

function BackupsTable({
    vm,
    isMultiNode,
    service_package_uuid,
}: {
    vm: ManagedServicesVmBody;
    service_package_uuid: string;
    isMultiNode: boolean;
}) {
    const { t } = useLingui();
    const mutation = useStandardMutation(rebuildServiceMutation);

    const items = sortByCreatedAtDesc(getReplicasByType(vm, "backup"));

    const latestItem = first(items);

    async function onRebuild(replica_uuid: string) {
        await mutation.mutateAsync({ location: vm.resource_location!, body: { service_package_uuid, replica_uuid } });
    }

    return (
        <>
            <DetailsTable>
                <DetailsTableRow title={t`Frequency`}>
                    <Trans comment="Backup frequency">Once a day</Trans>
                </DetailsTableRow>

                {!!latestItem && (
                    <DetailsTableRow title={<Trans comment="Timestamp of latest snapshot">Latest snapshot</Trans>}>
                        <LongDate date={latestItem.created_at} />
                    </DetailsTableRow>
                )}
            </DetailsTable>

            <WTable>
                <thead>
                    <tr>
                        <th>
                            <Trans>UUID</Trans>
                        </th>
                        <th>
                            <Trans>Size</Trans> (<Trans>GB</Trans>)
                        </th>
                        <th>
                            <Trans>Created</Trans>
                        </th>
                        {!isMultiNode && <th />}
                    </tr>
                </thead>
                <WTableBody>
                    {items.map(({ created_at, size, uuid }) => (
                        <tr key={uuid}>
                            <td>
                                <ClipBoardTooltip>{uuid}</ClipBoardTooltip>
                            </td>
                            <td>{size}</td>
                            <td>
                                <LongDate date={created_at} />
                            </td>
                            {!isMultiNode && (
                                <td className="text-right">
                                    <WModal button={<WModalButton label={t`Restore`} inTable icon="jp-return-icon" />}>
                                        <WModalContent
                                            title={t`Restore from backup`}
                                            label={t`Restore`}
                                            modalAction={() => onRebuild(uuid)}
                                        >
                                            <Trans>
                                                Restoring from a backup will overwrite the service's state with the
                                                selected backup's state.
                                            </Trans>
                                            <br />
                                            {uuid}
                                        </WModalContent>
                                    </WModal>
                                </td>
                            )}
                        </tr>
                    ))}
                </WTableBody>
            </WTable>
        </>
    );
}
